import { SamsScope, SamsVbl } from "@/models/samsVbl";
import { Competition, PdfData } from "@/models/pdfData";
import { SocketIoMessage } from "@/models/socketIo/socketIo";
import {
  getLocalActionHistory,
  updateLocalActionHistory,
} from "./localActionHistory";
import { UpdateAction } from "@/models/socketIo/updateAction";

const TOKEN_NEEDED_SCOPES: SamsScope[] = [];

export async function loadGameData({
  samsId,
  scope,
}: {
  samsId: string;
  scope?: SamsScope;
}): Promise<PdfData | undefined>;
export async function loadGameData({
  scope,
}: {
  scope?: SamsScope;
}): Promise<PdfData[]>;
export async function loadGameData({
  samsId,
  scope,
}: {
  samsId?: string;
  scope?: SamsScope;
}): Promise<PdfData[] | PdfData | undefined> {
  return doSamsRequest(scope)
    .then((json: SamsVbl): PdfData[] | PdfData | undefined => {
      const matchDaysMatches = json.matchDays.map((day) => day.matches).flat();
      const teams = Object.values(json.matchSeries)
        .map((matchSerie) => matchSerie.teams)
        .flat();
      const competitions: { [key in string]: Competition } = Object.values(
        json.matchSeries,
      )
        .map(({ id, gender, shortName, name }) => ({
          id,
          gender,
          shortName,
          name,
        }))
        .reduce((coll, comp) => ({ ...coll, [comp.id]: comp }), {});

      const allMatches: PdfData[] = matchDaysMatches
        .map((match) => {
          const team1Data = { ...teams.find(({ id }) => match?.team1 === id) };
          const team2Data = { ...teams.find(({ id }) => match?.team2 === id) };

          if (
            team1Data &&
            team2Data &&
            team1Data?.clubCode === team2Data?.clubCode
          ) {
            team1Data.clubCode = `${team1Data?.clubCode} ${team1Data?.letter}`;
            team2Data.clubCode = `${team2Data?.clubCode} ${team2Data?.letter}`;
          }

          const scoresheetPdfUrl =
            json.matchStates?.[match.id]?.scoresheetPdfUrl;
          const gameNumber =
            /https:\/\/distributor.sams-score.de\/scoresheet\/pdf\/[a-f0-9\-]*\/([0-9]*).pdf/.exec(
              scoresheetPdfUrl,
            )?.[1] || "";

          if (match) {
            return {
              samsId: match.id,
              scope: scope || "vbl",
              challenges: [],
              challengesUsed: {},
              gameData: {
                competition: competitions[match.matchSeries],
                gameStartDateTime: match?.date,
                scoresheetPdfUrl,
                location: "",
                gameNumber,
                team1: {
                  clubCode:
                    team1Data?.clubCode ||
                    match.teamDescription1.substring(0, 3),
                  longName: team1Data?.name || match.teamDescription1,
                  shortName:
                    team1Data?.shortName ||
                    team1Data?.clubCode ||
                    match.teamDescription1.substring(0, 3),
                  iconUrl: team1Data?.logoImage200,
                },
                team2: {
                  clubCode:
                    team2Data?.clubCode ||
                    match.teamDescription2.substring(0, 3),
                  longName: team2Data?.name || match.teamDescription2,
                  shortName:
                    team2Data?.shortName ||
                    team2Data?.clubCode ||
                    match.teamDescription2.substring(0, 3),
                  iconUrl: team2Data?.logoImage200,
                },
              },
            } as PdfData;
          } else {
            return undefined;
          }
        })
        .filter((data) => !!data);

      if (samsId !== undefined) {
        return allMatches.find((match) => samsId === match?.samsId);
      }
      return allMatches.filter((m) => m !== undefined);
    })
    .catch((error) => {
      console.error(error);
      return undefined;
    });
}

/*export const loadVblGameData = async (
  samsId: string,
  scope?: SamsScope,
): Promise<EnrichedVblGameData | null> =>
  doSamsRequest(scope).then((json: SamsVbl) => {
    const rawData = json.matchStates[samsId];
    if (rawData) {
      const teamSquads: TeamSquads = rawData.eventHistory
        .filter(({ type }) => type === "CONFIRM_TEAMSQUAD")
        .map((e) => e as EventHistoryConfirmTeamsquad)
        .sort((a, b) => a.timestamp - b.timestamp)
        .reduce(
          (prev, curr) => ({
            ...prev,
            ...{ [curr.teamCode]: curr.teamSquad },
          }),
          {} as TeamSquads,
        );
      return { rawData, additionalData: { teamSquads } };
    }

    return null;
  });*/

const doSamsRequest = async (scope?: SamsScope): Promise<SamsVbl> => {
  const myHeaders = new Headers();
  myHeaders.append("accept", "*/*");
  myHeaders.append("Connection", "keep-alive");

  const requestOptions: RequestInit = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  };

  return fetch(
    `${import.meta.env.VITE_PROXY_URL}/backend-sams-ticker/live/indoor/tickers/${scope || "vbl"}`,
    requestOptions,
  ).then((response) => response.json());
};

export const getActionHistory = async ({
  matchId,
  onData,
  samsScope,
  limitAction,
}: {
  matchId: string;
  onData: (data: SocketIoMessage) => void;
  samsScope: SamsScope;
  limitAction?: string | null;
}) => {
  const localHistory = getLocalActionHistory(matchId);
  const lastLocalAction = localHistory?.actionHistory.at(-1);
  const historyIsCurrent =
    lastLocalAction &&
    (await isHistoryCurrent(matchId, lastLocalAction, samsScope));

  try {
    let actionHistory: UpdateAction[] = historyIsCurrent
      ? localHistory?.actionHistory!
      : await fetchMatchHistory(matchId, samsScope);

    //let actionHistory: UpdateAction[] = challengeMock as UpdateAction[];

    if (limitAction && window) {
      const limitActionNumber = Number.parseInt(limitAction);
      // @ts-ignore
      window["allActions"] = actionHistory;

      actionHistory = actionHistory.filter(
        (a) => a.orderRank <= limitActionNumber,
      );
    }
    onData([
      "[SERVER] PULL ACTION HISTORY RESPONSE",
      { matchId, actionHistory },
    ]);
  } catch (e) {
    console.error("Failed to resync history", e);
  }
};

export const fetchMatchHistory = async (
  matchId: string,
  samsScope?: SamsScope,
): Promise<UpdateAction[]> => {
  const response = await fetch(
    `${import.meta.env.VITE_PROXY_URL}/backend-sams-score/api/match/history/${matchId}`,
    tokenNeeded(samsScope)
      ? { headers: { Authorization: `Bearer undefined` } }
      : undefined,
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch history: ${response.statusText}`);
  }
  const history: UpdateAction[] = await response.json();

  updateLocalActionHistory(matchId, history);

  return history;
};

export const isHistoryCurrent = async (
  matchId: string,
  lastUpdateAction: UpdateAction,
  samsScope: SamsScope,
): Promise<boolean> => {
  const response = await fetch(
    `${import.meta.env.VITE_PROXY_URL}/backend-sams-score/api/match/sync/${matchId}`,
    {
      headers: {
        ...(tokenNeeded(samsScope)
          ? { Authorization: `Bearer undefined` }
          : {}),
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({
        matchId,
        checksum: lastUpdateAction.checksum,
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch history: ${response.statusText}`);
  }
  return response.json().then((body) => body.inSync);
};

export const tokenNeeded = (scope?: SamsScope) =>
  scope && TOKEN_NEEDED_SCOPES.includes(scope);
